import { parseLocaleNumber } from "@household/shared/locale-numbers";

// One resolved locale for the whole web app (#1088 Phase 2).
//
// Before this, some views forced "en-US" and some passed `undefined` and
// followed the browser, so a user outside the US already saw mixed date formats
// across pages. Nothing here picks a locale per call site any more.
//
// IMPORTANT — what this is NOT for. Several places in the app use Intl to BUILD
// a value rather than to show one: `localDateInTimeZone` in shared/tasks.ts
// composes a YYYY-MM-DD string that drives occurrence scheduling, csv.ts parses
// bank exports, and the agent read-tools format money into the model's context.
// Those must keep a fixed locale. Localising a formatter whose output is stored,
// compared, or sent anywhere is how a cosmetic i18n change corrupts data.

/** What the user chose; mirrors themePreference's shape. */
export type LanguagePreference = "system" | "en" | "es";

/** What formatting code actually receives. A closed set — nothing else escapes. */
export type ResolvedLocale = "en" | "es";

const SUPPORTED: readonly ResolvedLocale[] = ["en", "es"];

function isSupported(value: string): value is ResolvedLocale {
  return (SUPPORTED as readonly string[]).includes(value);
}

/**
 * Resolve a stored preference to a locale we actually support.
 *
 * `systemLanguages` is injectable because the web unit harness is node-env with
 * no jsdom: `navigator` may genuinely not exist, and a bare reference would
 * throw rather than fall back.
 *
 * An unrecognised stored value — say "pt" written by a newer build — is treated
 * as "system" rather than passed through, so a raw tag can never reach Intl.
 */
export function resolveLocale(
  preference: string | null | undefined,
  systemLanguages?: readonly string[]
): ResolvedLocale {
  if (preference && isSupported(preference)) return preference;

  const candidates = systemLanguages
    ?? (typeof navigator !== "undefined"
      ? navigator.languages ?? (navigator.language ? [navigator.language] : [])
      : []);

  for (const candidate of candidates) {
    const primary = candidate.split("-")[0]?.toLowerCase() ?? "";
    if (isSupported(primary)) return primary;
  }
  return "en";
}

// The app-wide resolved locale, mirrored out of React so non-component helpers
// (money() in app/helpers.ts, and anything else that formats outside a render)
// can read it. React code should prefer the context value; this is the escape
// hatch for the handful of module-level helpers that predate it.
let currentLocale: ResolvedLocale = "en";

/**
 * Surfaces that hold user-typed NUMBERS as text and need to rewrite them when
 * the language changes.
 *
 * Text is not neutral: "1,234.56" is a price in English and unreadable in
 * Spanish. Nothing used to rewrite it, so a Spanish session kept showing
 * English text indefinitely, and the first keystroke into such a cell re-read
 * the whole old string as Spanish — turning $1,234.56 into $1.23 (codex
 * review found this five different ways). The stamp records which language the
 * text is in; this is how the text catches up.
 */
type LocaleListener = (locale: ResolvedLocale, previous: ResolvedLocale) => void;
const localeListeners = new Set<LocaleListener>();

export function onResolvedLocaleChange(listener: LocaleListener): () => void {
  localeListeners.add(listener);
  return () => localeListeners.delete(listener);
}

export function setResolvedLocale(locale: ResolvedLocale): void {
  const previous = currentLocale;
  currentLocale = locale;
  if (previous === locale) return;
  for (const listener of localeListeners) listener(locale, previous);
}

export function getResolvedLocale(): ResolvedLocale {
  return currentLocale;
}

/**
 * Currency stays USD while the formatting locale changes.
 *
 * `narrowSymbol` is deliberate: under "es" the default currencyDisplay renders
 * USD as "1.234,56 US$", which reads as a foreign-exchange amount rather than a
 * price. narrowSymbol gives "1.234,56 $".
 */
export function currencyFormat(locale: ResolvedLocale, currency = "USD"): Intl.NumberFormat {
  // narrowSymbol ONLY for the household's own currency.
  //
  // The reason for it is specific to USD: under "es" the default renders USD as
  // "1234,56 US$", which reads as a foreign-exchange amount rather than a
  // price. It does not generalise — narrowSymbol renders USD, CAD and AUD all
  // as "$1,234.56" in English and "1234,56 $" in Spanish, so applying it to
  // every currency made three different amounts of money indistinguishable on
  // finance accounts, transactions, budgets, projects and shopping. Everything
  // else keeps the default, which is "CA$"/"A$" in English and the ISO code in
  // Spanish. EUR and GBP render identically either way.
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    currencyDisplay: currencyDisplayFor(currency)
  });
}

/**
 * The `currencyDisplay` any money formatter must use.
 *
 * Exported because `currencyFormat` fixes its fraction digits, so a caller that
 * needs different ones has to build its own Intl instance — and the last time
 * one did, it hardcoded narrowSymbol and reintroduced exactly the collision
 * this rule exists to prevent. There is one rule and this is it.
 */
export function currencyDisplayFor(currency: string): "narrowSymbol" | "symbol" {
  // Case-insensitive: Intl accepts "usd" and normalises it, so a stored code in
  // lower case would otherwise take the wrong branch and render the household's
  // own currency as "1234,56 US$" under es — the exact reading this rule exists
  // to avoid.
  return currency.toUpperCase() === "USD" ? "narrowSymbol" : "symbol";
}

/*
 * Display formatters bound to the resolved locale (#1088 Phase 2).
 *
 * Call these at RENDER time, never at module scope. The locale is not known
 * until /auth/status answers, so a module-level `new Intl.DateTimeFormat(...)`
 * captures whatever the locale was at import — always "en" — and never updates
 * when the user picks Spanish. That is the bug this replaces, not a style
 * preference.
 *
 * Constructing an Intl formatter is genuinely expensive, so instances are
 * memoized per locale + options. The locale is part of the key, so switching
 * language does not need to invalidate anything.
 */
const formatterCache = new Map<string, Intl.DateTimeFormat | Intl.NumberFormat>();

export function dateTimeFormat(options: Intl.DateTimeFormatOptions = {}): Intl.DateTimeFormat {
  const locale = getResolvedLocale();
  const key = `date|${locale}|${JSON.stringify(options)}`;
  const hit = formatterCache.get(key);
  if (hit) return hit as Intl.DateTimeFormat;
  const made = new Intl.DateTimeFormat(locale, options);
  formatterCache.set(key, made);
  return made;
}

/**
 * "3 minutes ago" in whatever language the app is in.
 *
 * `numeric: "auto"` is what turns -1 day into "yesterday" and "ayer" rather
 * than "1 day ago" — the idiomatic form each locale actually uses, which is the
 * whole reason not to assemble these by hand.
 */
export function relativeTimeFormat(options: Intl.RelativeTimeFormatOptions = {}): Intl.RelativeTimeFormat {
  const locale = getResolvedLocale();
  const key = `relative|${locale}|${JSON.stringify(options)}`;
  const hit = relativeFormatterCache.get(key);
  if (hit) return hit;
  const made = new Intl.RelativeTimeFormat(locale, { numeric: "auto", ...options });
  relativeFormatterCache.set(key, made);
  return made;
}

// Separate from formatterCache only because its value type differs; same
// locale-in-the-key rule, so switching language invalidates nothing.
const relativeFormatterCache = new Map<string, Intl.RelativeTimeFormat>();

export function numberFormat(options: Intl.NumberFormatOptions = {}): Intl.NumberFormat {
  const locale = getResolvedLocale();
  const key = `number|${locale}|${JSON.stringify(options)}`;
  const hit = formatterCache.get(key);
  if (hit) return hit as Intl.NumberFormat;
  const made = new Intl.NumberFormat(locale, options);
  formatterCache.set(key, made);
  return made;
}

/**
 * Format a timestamp for display, or return `fallback` when it is not one.
 *
 * `Intl.DateTimeFormat.format` THROWS a RangeError on an invalid date, where
 * the `toLocaleString` calls this replaced merely rendered "Invalid Date". That
 * difference turned a malformed timestamp from a cosmetic blemish into a
 * crashed render, so every site that formats a value straight off the API goes
 * through here. Sites that already check the date themselves do not need it.
 */
export function formatDateTime(
  value: string | number | Date,
  options: Intl.DateTimeFormatOptions = {},
  fallback = ""
): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return dateTimeFormat(options).format(date);
}

/**
 * Format a DATE-ONLY value — the "YYYY-MM-DD" the API stores for a due date, an
 * expiry, a transaction day.
 *
 * Anchored at midday before formatting, which is the whole reason this exists
 * rather than each call site doing it. `new Date("2026-08-15")` parses as
 * MIDNIGHT UTC, so rendering it in any timezone west of Greenwich shows the
 * 14th — a date-only value silently sliding back a day is worse than the
 * unformatted string it replaced.
 *
 * Returns `fallback` (by default the raw value, so nothing disappears) when the
 * input is not a date.
 */
/**
 * Put a number INTO a text field the app will later parse back.
 *
 * Different job from `numberFormat`, which is for prose and rounds to something
 * readable. This one must be LOSSLESS, because whatever it writes is what the
 * parser reads and the API is then told. Formatting a stored 1.23456 with four
 * fraction digits produced "1.2346", and editing any other field in the row
 * wrote that rounded value back over the real one — a silent, permanent edit
 * nobody made.
 *
 * Grouping is off for the same reason: a field is not a sentence, and the fewer
 * separators the round trip carries the fewer ways it can disagree with itself.
 *
 * Its one limit, stated rather than glossed: Intl caps fraction digits at 20, so
 * a magnitude below 1e-20 formats as "0". No household quantity or price lives
 * there, and callers no longer depend on this being exact for values a user has
 * not touched — an unedited cell keeps the number the server sent rather than
 * re-reading its own text. This is the display half, not the source of truth.
 */
/**
 * The same number, written the way `to` writes it, or null when the text cannot
 * be read as a number in the language it was written in.
 *
 * Null means LEAVE IT ALONE. A half-typed "1," is not readable and must not be
 * rewritten out from under the cursor; it stays as it is, still labelled with
 * the language it was typed in, and stays correct that way.
 */
export function reformatNumberText(
  text: string,
  from: TypedIn,
  to: TypedIn,
  options: Intl.NumberFormatOptions = { maximumFractionDigits: 20, useGrouping: false }
): string | null {
  if (!text.trim()) return null;
  const value = parseLocaleNumber(text, from);
  if (value == null) return null;

  // Only text this app itself would have WRITTEN is rewritten. Anything else is
  // half-typed or hand-shaped — "1." on the way to "1.5", or "2.50" where the
  // formatter says "2.5" — and rewriting it would edit the user's own
  // characters, or move the caret, on an event they did not cause. Leaving it
  // is safe: the stamp still says which language it is in.
  if (new Intl.NumberFormat(from, options).format(value) !== text.trim()) return null;
  return new Intl.NumberFormat(to, options).format(value);
}

/**
 * The language to stamp an edit to a plain field with — one that is not a sheet
 * cell and so has no caret latch.
 *
 * Text keeps the language it was being typed in; only an EMPTY field adopts the
 * current one. Without this, a language change arriving mid-number re-labelled
 * the whole string on the next keystroke: an English "1,2" completed as "1,234"
 * was read as 1.234 (codex review). Clearing the field starts a new
 * composition, which is what an empty `before` means.
 */
export function stampForEdit(before: string, existing: TypedIn): TypedIn {
  return before.trim() ? existing : getResolvedLocale();
}

export function formatNumberForInput(value: number): string {
  return numberFormat({ maximumFractionDigits: 20, useGrouping: false }).format(value);
}

export function formatDateOnly(
  value: string | null | undefined,
  options: Intl.DateTimeFormatOptions = { month: "short", day: "numeric", year: "numeric" },
  fallback?: string
): string {
  if (!value) return fallback ?? "";
  return formatDateTime(`${value}T12:00:00`, options, fallback ?? value);
}

/**
 * Format a RATIO as a percentage — `0.085` renders "8.5%", not "0.1%".
 *
 * `style: "percent"` does the scaling itself, which is the point: it also
 * places the sign and the symbol the way the locale does. Spanish separates
 * them with a non-breaking space ("8,5 %"), so the hand-rolled
 * `${n.toFixed(1)}%` this replaces was wrong in Spanish on both counts —
 * decimal point and spacing — even where the digits happened to match.
 *
 * Call sites that hold a number already scaled to 0-100 must divide by 100
 * first; there is no second entry point, because a helper that guesses the
 * scale from the magnitude gets 0.5% and 50% the wrong way round.
 */
export function formatPercent(ratio: number, options: Intl.NumberFormatOptions = {}): string {
  return numberFormat({
    style: "percent",
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
    ...options
  }).format(ratio);
}

/*
 * The typed-text parser now lives in `@household/shared/locale-numbers` (#1121)
 * and is re-exported here, so every call site that imports it from this module
 * keeps working and there is still exactly one implementation.
 *
 * It moved because `parseLength` in the floor-plan units module reads the same
 * class of field — a plain `<input inputMode="decimal">` the app owns both
 * halves of — and shared code cannot import out of an app. The rule about which
 * fields need it, and which `<input type="number">` fields must NOT have it,
 * travelled with the function; read it there before wiring a new field.
 */
export { parseLocaleNumber } from "@household/shared/locale-numbers";

/**
 * The convention a piece of typed text was written in.
 *
 * Two attempts to work this out AFTER the fact both failed, and the failures are
 * worth recording because they look reasonable:
 *
 * Guessing by trying the other locale when the current one refuses does not
 * work, because the dangerous strings are the ones BOTH locales accept. An
 * English "1,234" meaning one thousand two hundred thirty four is read by
 * Spanish as 1.234 without complaint, so a price becomes 123 cents and no
 * fallback ever runs.
 *
 * Rewriting separators inside free text does not work either, because a
 * separator between two digits is not necessarily a number: "Modelo 1.234" is a
 * NAME, and rewriting it stores a different product.
 *
 * So intent is recorded rather than inferred. A field that holds typed text
 * remembers the locale it was typed in and is read back with that one, whatever
 * the app has switched to since — which is what the person actually meant.
 */
export type TypedIn = ResolvedLocale;
